import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { i18n, ready } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  
  // Initialize from localStorage or default to Latvian
  const getInitialLanguage = () => {
    const saved = localStorage.getItem('i18nextLng');
    const supportedLanguages = ['en', 'ru', 'et', 'lt', 'lv'];
    if (saved && supportedLanguages.includes(saved)) {
      return saved;
    }
    return 'lv';
  };
  
  const [currentLanguage, setCurrentLanguage] = useState(getInitialLanguage());
  const [isInitialized, setIsInitialized] = useState(false);

  const supportedLanguages = useMemo(() => ['en', 'ru', 'et', 'lt', 'lv'], []);

  // Extract language from URL path
  const getLanguageFromPath = useCallback((pathname) => {
    // First check if we have a lang param from the route
    if (params.lang && supportedLanguages.includes(params.lang)) {
      return params.lang;
    }
    
    // Fallback to parsing pathname
    const pathSegments = pathname.split('/').filter(Boolean);
    const firstSegment = pathSegments[0];
    
    if (supportedLanguages.includes(firstSegment)) {
      return firstSegment;
    }
    return null;
  }, [params.lang, supportedLanguages]);

  // Get path without language prefix
  const getPathWithoutLanguage = useCallback((pathname) => {
    const pathSegments = pathname.split('/').filter(Boolean);
    const firstSegment = pathSegments[0];
    
    if (supportedLanguages.includes(firstSegment)) {
      return '/' + pathSegments.slice(1).join('/');
    }
    return pathname;
  }, [supportedLanguages]);

  // Get translated route path
  const getTranslatedPath = (routeKey, language = currentLanguage) => {
    const routeTranslation = i18n.getResource(language, 'translation', `routes.${routeKey}`);
    if (routeTranslation) {
      return `/${language}/${routeTranslation}`;
    }
    return `/${language}`;
  };

  // Change language and update URL
  const changeLanguage = (newLanguage) => {
    if (supportedLanguages.includes(newLanguage)) {
      const currentPath = getPathWithoutLanguage(location.pathname);
      const newPath = currentPath === '/' ? `/${newLanguage}` : `/${newLanguage}${currentPath}`;
      
      setCurrentLanguage(newLanguage);
      i18n.changeLanguage(newLanguage);
      // Save to localStorage for persistence
      localStorage.setItem('i18nextLng', newLanguage);
      navigate(newPath, { replace: true });
    }
  };

  // Navigate to a route in current language
  const navigateToRoute = (routeKey, params = {}) => {
    let path = getTranslatedPath(routeKey);
    
    // Replace route parameters
    Object.keys(params).forEach(key => {
      path = path.replace(`:${key}`, params[key]);
    });
    
    navigate(path);
  };

  // Initialize language from URL or localStorage on mount and route changes
  useEffect(() => {
    // Wait for i18n to be ready before initializing language
    if (!ready) return;
    
    const languageFromPath = getLanguageFromPath(location.pathname);
    const savedLanguage = localStorage.getItem('i18nextLng');
    const currentI18nLang = i18n.language;
    
    if (languageFromPath) {
      // If we found a language in the path, use it and save it
      if (languageFromPath !== currentI18nLang) {
        setCurrentLanguage(languageFromPath);
        i18n.changeLanguage(languageFromPath);
        // Save to localStorage for persistence
        localStorage.setItem('i18nextLng', languageFromPath);
      }
      // Always initialize if we have a valid language in the path
      if (!isInitialized) {
      setIsInitialized(true);
      }
    } else if (location.pathname === '/') {
      // If no language in URL and we're at root, check localStorage first
      const defaultLang = savedLanguage && supportedLanguages.includes(savedLanguage) ? savedLanguage : 'lv';
      
      if (savedLanguage && supportedLanguages.includes(savedLanguage) && savedLanguage !== currentI18nLang) {
        setCurrentLanguage(savedLanguage);
        i18n.changeLanguage(savedLanguage);
      }
      
      navigate(`/${defaultLang}`, { replace: true });
      if (!isInitialized) {
      setIsInitialized(true);
      }
    } else {
      // If we're on a path without language prefix, redirect to include saved language
      // This ensures language persists across all pages
      const langToUse = savedLanguage && supportedLanguages.includes(savedLanguage) ? savedLanguage : 'lv';
      const pathWithoutLang = getPathWithoutLanguage(location.pathname);
      const newPath = pathWithoutLang === '/' ? `/${langToUse}` : `/${langToUse}${pathWithoutLang}`;
      
      if (location.pathname !== newPath) {
        if (langToUse !== currentI18nLang) {
          setCurrentLanguage(langToUse);
          i18n.changeLanguage(langToUse);
          localStorage.setItem('i18nextLng', langToUse);
        }
        navigate(newPath, { replace: true });
      }
      if (!isInitialized) {
      setIsInitialized(true);
      }
    }
  }, [location.pathname, params.lang, i18n, navigate, getLanguageFromPath, ready, supportedLanguages, getPathWithoutLanguage, isInitialized]);

  // Show loading state until i18n is ready and language is initialized
  if (!ready || !isInitialized) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const value = {
    currentLanguage,
    supportedLanguages,
    changeLanguage,
    navigateToRoute,
    getTranslatedPath,
    getLanguageFromPath,
    getPathWithoutLanguage
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

LanguageProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
